/**
 * THRESHOLDS PAGE COMPONENT - Medical Alert Threshold Management Interface
 * 
 * This component provides a comprehensive interface for managing medical alert
 * thresholds for vital signs monitoring. It allows doctors to customize patient-specific
 * thresholds based on individual medical conditions and clinical guidelines.
 * 
 * Key Features:
 * - Patient-specific threshold customization
 * - Multi-level alert system (low, medium, high, critical)
 * - Blood pressure stage classification (Stage 1, 2, 3)
 * - Heart rate variability monitoring thresholds
 * - Temperature fever and hypothermia detection
 * - Oxygen saturation critical level monitoring
 * - Form validation and error handling
 * - Real-time threshold updates
 * 
 * Medical Standards:
 * - Heart Rate: Normal (60-100 BPM), Bradycardia (<60), Tachycardia (>100)
 * - Blood Pressure: Normal, Stage 1, Stage 2, Stage 3 hypertension
 * - Temperature: Normal (36-37.3°C), Fever (>37.3°C), High fever (>39.1°C)
 * - SpO2: Normal (>94%), Low (90-94%), Critical (<90%)
 * - Respiratory Rate: Normal (12-20 BPM), Abnormal (<12 or >20)
 * - HRV: Individual variability based on patient baseline
 * 
 * User Interface:
 * - Threshold input forms with number validation
 * - Patient information display
 * - Save and reset functionality
 * - Navigation and breadcrumb support
 * - Loading states and error handling
 */

// React core imports for component functionality and state management
import React, { useState, useEffect } from 'react';

// API imports for threshold management operations
import { Thresholds, loadThresholds, saveThresholds, saveDoctorPresetThresholds, getDoctorPresetThresholds, defaultThresholds } from '../api/thresholds';
import { getPatientInfo, getDoctorByEmail } from '../api/database';

// Page navigation type definition for application routing
type Page = 'login' | 'register' | 'dashboard' | 'history' | 'settings' | 'consent' | 'thresholds';

/**
 * THRESHOLDS PAGE PROPS INTERFACE
 * 
 * Defines the props structure for the ThresholdsPage component,
 * including navigation callbacks and patient identification.
 */
type ThresholdsPageProps = {
  onNavigate: (page: Page) => void;  // Callback function for page navigation
  patId?: string;                   // Optional patient ID for threshold customization
  previousPage?: any;       // Previous page for back navigation
  isDemo?: boolean;                 // Demo mode flag for hardcoded data
  presetMode?: boolean;             // When true, doctor edits their preset thresholds (no patient)
  userRole?: string;                // Used with presetMode to load/save doctor preset
};

type SectionProps = React.PropsWithChildren<{ title: string }>;
const Section: React.FC<SectionProps> = ({ title, children }) => (
  <div style={{
    backgroundColor: 'white',
    border: '1px solid #e9ecef',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    transition: 'box-shadow 0.2s ease'
  }}>
    <h3 style={{
      margin: '0 0 16px 0',
      fontSize: '1.1rem',
      fontWeight: '600',
      color: '#495057',
      borderBottom: '2px solid #f8f9fa',
      paddingBottom: '8px'
    }}>
      {title}
    </h3>
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
      gap: '16px' 
    }}>
      {children}
    </div>
  </div>
);

const NumberInput: React.FC<{ label: string; value: number; onChange: (v: number) => void; disabled?: boolean }>
  = ({ label, value, onChange, disabled = false }) => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  }}>
    <label style={{
      fontSize: '0.85rem',
      fontWeight: '500',
      color: '#6c757d',
      margin: 0
    }}>
      {label}
    </label>
    <input 
      style={{
        padding: '10px 12px',
        border: '2px solid #e9ecef',
        borderRadius: '8px',
        fontSize: '1rem',
        fontWeight: '600',
        color: disabled ? '#6c757d' : '#495057',
        backgroundColor: disabled ? '#f8f9fa' : '#fff',
        transition: 'all 0.2s ease',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'text'
      }}
      className="no-spin" 
      type="number" 
      value={value} 
      onChange={e => !disabled && onChange(Number(e.target.value))}
      disabled={disabled}
      onFocus={(e) => {
        if (!disabled) {
          e.target.style.borderColor = '#667eea';
          e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
        }
      }}
      onBlur={(e) => {
        if (!disabled) {
          e.target.style.borderColor = '#e9ecef';
          e.target.style.boxShadow = 'none';
        }
      }}
    />
  </div>
);

const ThresholdsPage: React.FC<ThresholdsPageProps> = ({ onNavigate, patId, previousPage, isDemo = false, presetMode = false, userRole }) => {
  const [t, setT] = useState<Thresholds>(defaultThresholds);
  const [loading, setLoading] = useState(true);
  const [patientInfo, setPatientInfo] = useState<{ pat_id: string; first_name: string; last_name: string; dev_id: string } | null>(null);
  const [doctorPresetNotFound, setDoctorPresetNotFound] = useState(false);   // true when doctor has no preset yet
  const [showPresetCreateForm, setShowPresetCreateForm] = useState(false);  // true when user chose "Add my preset"
  const update = <K extends keyof Thresholds>(k: K, v: Thresholds[K]) => setT(prev => ({ ...prev, [k]: v }));

  const isDoctorPreset = Boolean(presetMode && userRole === 'doctor');
  const showPresetForm = isDoctorPreset && (!doctorPresetNotFound || showPresetCreateForm);

  // Load thresholds (doctor preset or patient) when component mounts or deps change
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        if (isDoctorPreset) setShowPresetCreateForm(false);
        
        if (isDemo) {
          setT(defaultThresholds);
          setPatientInfo({ pat_id: '999', first_name: 'Demo', last_name: 'User', dev_id: 'DEMO-001' });
          setDoctorPresetNotFound(false);
          return;
        }

        if (isDoctorPreset) {
          const email = localStorage.getItem('currentUserEmail') || '';
          const doc = await getDoctorByEmail(email);
          setPatientInfo(null);
          if (doc?.doc_id) {
            const preset = await getDoctorPresetThresholds(doc.doc_id);
            if (preset) {
              setT(preset);
              setDoctorPresetNotFound(false);
            } else {
              setDoctorPresetNotFound(true);
            }
          } else {
            setDoctorPresetNotFound(true);
          }
          return;
        }

        setDoctorPresetNotFound(false);
        const currentPatId = patId || localStorage.getItem('selectedPatId');
        if (currentPatId) {
          const [patientThresholds, patientData] = await Promise.all([
            loadThresholds(currentPatId),
            getPatientInfo(currentPatId)
          ]);
          setT(patientThresholds);
          setPatientInfo(patientData);
        } else {
          setT(defaultThresholds);
          setPatientInfo(null);
        }
      } catch (error) {
        console.error('[ThresholdsPage] Error loading data:', error);
        setT(defaultThresholds);
        setPatientInfo(null);
        setDoctorPresetNotFound(false);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [patId, isDemo, isDoctorPreset]);

  return (
    <div className="dashboard">
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="hamburger-btn" onClick={() => onNavigate(previousPage || 'dashboard')} aria-label="Back">←</button>
          <h1>Thresholds</h1>
        </div>
        <div />
      </header>

      <div className="history-container thresholds-narrow">
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <div>{isDoctorPreset ? 'Loading preset...' : 'Loading patient data...'}</div>
          </div>
        ) : isDoctorPreset && doctorPresetNotFound && !showPresetCreateForm ? (
          /* No preset found – show explanation and Add button */
          <div style={{
            backgroundColor: 'white',
            border: '1px solid #e9ecef',
            borderRadius: '12px',
            padding: '32px 24px',
            marginBottom: '24px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 20px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2rem'
            }}>
              🌡️
            </div>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '1.35rem', fontWeight: '600', color: '#1565c0' }}>
              What are Preset Thresholds?
            </h2>
            <p style={{ margin: '0 0 16px 0', fontSize: '1rem', color: '#455a64', lineHeight: 1.5, maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto' }}>
              Preset thresholds are the default alert limits that apply to all patients you assign to yourself. You can set them once and they will be used automatically for new patients.
            </p>
            <p style={{ margin: '0 0 24px 0', fontSize: '0.95rem', color: '#78909c', fontWeight: '500' }}>
              No preset thresholds were found for your account.
            </p>
            <button
              type="button"
              onClick={() => {
                setT(defaultThresholds);
                setShowPresetCreateForm(true);
              }}
              style={{
                padding: '14px 28px',
                border: 'none',
                borderRadius: '10px',
                backgroundColor: '#1976d2',
                color: 'white',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.35)'
              }}
            >
              Add my preset thresholds
            </button>
          </div>
        ) : (
          <>
            {/* Doctor preset header (when editing/creating own preset) */}
            {showPresetForm && (
              <div style={{
                backgroundColor: '#e8f4fd',
                border: '1px solid #90caf9',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.25rem',
                  flexShrink: 0
                }}>
                  🌡️
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: '0 0 6px 0', fontSize: '1.25rem', fontWeight: '600', color: '#1565c0' }}>
                    {showPresetCreateForm ? 'Define your pre-set thresholds' : 'Your pre-set thresholds'}
                  </h2>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#1976d2' }}>
                    {showPresetCreateForm
                      ? 'These values will be used as defaults for all patients assigned to you.'
                      : 'These values are used as defaults for all patients you assign to yourself.'}
                  </p>
                </div>
              </div>
            )}
            {/* Patient Information Header */}
            {patientInfo && (
              <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #e9ecef',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '1.5rem',
                  fontWeight: 'bold',
                  flexShrink: 0
                }}>
                  {patientInfo.first_name.charAt(0).toUpperCase()}{patientInfo.last_name.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '1.5rem', 
                    fontWeight: '600', 
                    color: '#2c3e50' 
                  }}>
                    {patientInfo.first_name} {patientInfo.last_name}
                  </h2>
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    fontSize: '0.9rem', 
                    color: '#6c757d' 
                  }}>
                    <span>
                      <strong>Patient ID:</strong> {patientInfo.pat_id}
                    </span>
                    <span>
                      <strong>Device ID:</strong> {patientInfo.dev_id}
                    </span>
                  </div>
                </div>
                <div style={{
                  color: '#28a745',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  backgroundColor: '#d4edda',
                  padding: '6px 12px',
                  borderRadius: '20px',
                  border: '1px solid #c3e6cb'
                }}>
                  Thresholds Active
                </div>
              </div>
            )}

        {/* Demo mode info message */}
        {isDemo && (
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #90caf9',
            borderRadius: '8px',
            color: '#1565c0',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>ℹ️</span>
            <span>Demo Mode: Viewing default thresholds. Editing is disabled in demo mode.</span>
          </div>
        )}

        <Section title="Heart Rate (bpm)">
          <NumberInput label="Critical Low" value={t.heartRate.low} onChange={v => update('heartRate', { ...t.heartRate, low: v })} disabled={isDemo} />
          <NumberInput label="Resting High" value={t.heartRate.medium} onChange={v => update('heartRate', { ...t.heartRate, medium: v })} disabled={isDemo} />
          <NumberInput label="Critical High" value={t.heartRate.high} onChange={v => update('heartRate', { ...t.heartRate, high: v })} disabled={isDemo} />
        </Section>

        <Section title="Heart Rate Variability (ms)">
          <NumberInput label="High" value={t.hrv.high} onChange={v => update('hrv', { ...t.hrv, high: v })} disabled={isDemo} />
          <NumberInput label="Resting Low" value={t.hrv.medium} onChange={v => update('hrv', { ...t.hrv, medium: v })} disabled={isDemo} />
          <NumberInput label="Critical Low" value={t.hrv.low} onChange={v => update('hrv', { ...t.hrv, low: v })} disabled={isDemo} />
        </Section>

        <Section title="SpO2 (%)">
          <NumberInput label="Low" value={t.spo2.medium} onChange={v => update('spo2', { ...t.spo2, medium: v })} disabled={isDemo} />
          <NumberInput label="Critical Low" value={t.spo2.low} onChange={v => update('spo2', { ...t.spo2, low: v })} disabled={isDemo} />
        </Section>

        <Section title="Breathing (breaths/min)">
          <NumberInput label="Low" value={t.breathing.low} onChange={v => update('breathing', { ...t.breathing, low: v })} disabled={isDemo} />
          <NumberInput label="Medium" value={t.breathing.medium} onChange={v => update('breathing', { ...t.breathing, medium: v })} disabled={isDemo} />
          <NumberInput label="High" value={t.breathing.high} onChange={v => update('breathing', { ...t.breathing, high: v })} disabled={isDemo} />
        </Section>

        <Section title="Blood Pressure (mmHg)">
          <NumberInput label="Systolic Low" value={t.bloodPressure.systolic.low} onChange={v => update('bloodPressure', { ...t.bloodPressure, systolic: { ...t.bloodPressure.systolic, low: v } })} disabled={isDemo} />
          <NumberInput label="Systolic Stage1" value={t.bloodPressure.systolic.systolic_stage1} onChange={v => update('bloodPressure', { ...t.bloodPressure, systolic: { ...t.bloodPressure.systolic, systolic_stage1: v } })} disabled={isDemo} />
          <NumberInput label="Systolic Stage2" value={t.bloodPressure.systolic.systolic_stage2} onChange={v => update('bloodPressure', { ...t.bloodPressure, systolic: { ...t.bloodPressure.systolic, systolic_stage2: v } })} disabled={isDemo} />
          {/* <NumberInput label="Systolic Stage3" value={t.bloodPressure.systolic.systolic_stage3} onChange={v => update('bloodPressure', { ...t.bloodPressure, systolic: { ...t.bloodPressure.systolic, systolic_stage3: v } })} disabled={isDemo} /> */}
          <NumberInput label="Systolic High" value={t.bloodPressure.systolic.high} onChange={v => update('bloodPressure', { ...t.bloodPressure, systolic: { ...t.bloodPressure.systolic, high: v } })} disabled={isDemo} />
          <NumberInput label="Diastolic Low" value={t.bloodPressure.diastolic.low} onChange={v => update('bloodPressure', { ...t.bloodPressure, diastolic: { ...t.bloodPressure.diastolic, low: v } })} disabled={isDemo} />
          <NumberInput label="Diastolic Stage1" value={t.bloodPressure.diastolic.diastolic_stage1} onChange={v => update('bloodPressure', { ...t.bloodPressure, diastolic: { ...t.bloodPressure.diastolic, diastolic_stage1: v } })} disabled={isDemo} />
          <NumberInput label="Diastolic Stage2" value={t.bloodPressure.diastolic.diastolic_stage2} onChange={v => update('bloodPressure', { ...t.bloodPressure, diastolic: { ...t.bloodPressure.diastolic, diastolic_stage2: v } })} disabled={isDemo} />
          {/* <NumberInput label="Diastolic Stage3" value={t.bloodPressure.diastolic.diastolic_stage3} onChange={v => update('bloodPressure', { ...t.bloodPressure, diastolic: { ...t.bloodPressure.diastolic, diastolic_stage3: v } })} disabled={isDemo} /> */}
          <NumberInput label="Diastolic High" value={t.bloodPressure.diastolic.high} onChange={v => update('bloodPressure', { ...t.bloodPressure, diastolic: { ...t.bloodPressure.diastolic, high: v } })} disabled={isDemo} />
        </Section>

        <Section title="Skin Temperature (°C)">
          <NumberInput label="Low" value={t.temperature.low} onChange={v => update('temperature', { ...t.temperature, low: v })} disabled={isDemo} />
          <NumberInput label="Medium" value={t.temperature.medium} onChange={v => update('temperature', { ...t.temperature, medium: v })} disabled={isDemo} />
          <NumberInput label="High" value={t.temperature.high} onChange={v => update('temperature', { ...t.temperature, high: v })} disabled={isDemo} />
        </Section>

        {/* Action buttons - disabled in demo mode */}
        {!isDemo && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '20px 0',
            borderTop: '1px solid #e9ecef',
            marginTop: '20px'
          }}>
            <button 
              style={{
                padding: '12px 24px',
                border: '2px solid #6c757d',
                borderRadius: '8px',
                backgroundColor: 'white',
                color: '#6c757d',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => setT(defaultThresholds)}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#495057';
                e.currentTarget.style.color = '#495057';
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#6c757d';
                e.currentTarget.style.color = '#6c757d';
                e.currentTarget.style.backgroundColor = 'white';
              }}
            >
              Reset to Defaults
            </button>
            <button 
              style={{
                padding: '12px 32px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: '#667eea',
                color: 'white',
                fontSize: '0.95rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
              }}
              onClick={async () => { 
                let success = false;
                if (isDoctorPreset) {
                  const email = localStorage.getItem('currentUserEmail') || '';
                  const doc = await getDoctorByEmail(email);
                  if (doc?.doc_id) {
                    success = await saveDoctorPresetThresholds(doc.doc_id, t);
                  }
                } else {
                  success = await saveThresholds(t, patId || localStorage.getItem('selectedPatId') || undefined);
                }
                if (success) {
                  onNavigate(previousPage || 'settings');
                } else {
                  alert('Failed to save thresholds. Please try again.');
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#5a6fd8';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#667eea';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
              }}
            >
              Save Changes
            </button>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default ThresholdsPage;


